import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import AlertComponent from "../../components/AlertComponent";
import Modal from "../../components/Modal";
import { EmptyState, PageHeader, Panel } from "../../components/Page";
import StatusBadge from "../../components/StatusBadge";
import { apiUrl, getPayload } from "../../services/apiConfig";
import { errorMessage } from "../../utils/formatters";

const emptyAuth = {
  descricao: "",
  protegidaPorJwt: false,
  usuarioJwt: "",
  senhaJwt: "",
  urlChamadaJwt: "",
  campoUsuarioJwt: "usuario",
  campoSenhaJwt: "senha",
  caminhoTokenJwt: "token",
  prefixoAutorizacaoJwt: "Bearer",
};

function fromAuth(auth) {
  if (!auth) return emptyAuth;

  return {
    descricao: auth.descricao ?? "",
    protegidaPorJwt: Boolean(auth.protegidaPorJwt),
    usuarioJwt: auth.usuarioJwt ?? "",
    senhaJwt: "",
    urlChamadaJwt: auth.urlChamadaJwt ?? "",
    campoUsuarioJwt: auth.campoUsuarioJwt ?? "usuario",
    campoSenhaJwt: auth.campoSenhaJwt ?? "senha",
    caminhoTokenJwt: auth.caminhoTokenJwt ?? "token",
    prefixoAutorizacaoJwt: auth.prefixoAutorizacaoJwt ?? "Bearer",
  };
}

function AutenticacaoForm({ initialAuth, onCancel, onSave }) {
  const [form, setForm] = useState(() => fromAuth(initialAuth));
  const campoUsuario = form.campoUsuarioJwt || "usuario";
  const campoSenha = form.campoSenhaJwt || "senha";
  const payloadPreview = JSON.stringify(
    {
      [campoUsuario]: form.usuarioJwt || "fabio",
      [campoSenha]: form.senhaJwt ? "********" : "teste",
    },
    null,
    2
  );

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({
      ...form,
      usuarioJwt: form.protegidaPorJwt ? form.usuarioJwt || null : null,
      senhaJwt: form.protegidaPorJwt ? form.senhaJwt || null : null,
      urlChamadaJwt: form.protegidaPorJwt ? form.urlChamadaJwt || null : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Descrição</label>
        <input value={form.descricao} onChange={(event) => setField("descricao", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
      </div>

      <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
        <input type="checkbox" checked={form.protegidaPorJwt} onChange={(event) => setField("protegidaPorJwt", event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
        Protegida por JWT
      </label>

      {form.protegidaPorJwt && (
        <div className="space-y-4 rounded-lg border border-slate-200 p-4">
          <div className="rounded-lg border border-violet-100 bg-violet-50 px-4 py-3 text-sm text-violet-900">
            Esta configuração chama primeiro a URL de token, lê o JWT da resposta e depois envia o job com o header Authorization.
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Valor do login/usuário</label>
              <input value={form.usuarioJwt} onChange={(event) => setField("usuarioJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
              <p className="mt-1 text-xs text-slate-500">Ex.: fabio</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Valor da senha</label>
              <input type="password" value={form.senhaJwt} onChange={(event) => setField("senhaJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required={form.protegidaPorJwt} />
              <p className="mt-1 text-xs text-slate-500">Ex.: teste</p>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">URL para gerar JWT</label>
            <input value={form.urlChamadaJwt} onChange={(event) => setField("urlChamadaJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="https://..." required />
            <p className="mt-1 text-xs text-slate-500">Informe o endpoint exato que recebe o payload de login e retorna o token.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Campo do login no payload</label>
              <input value={form.campoUsuarioJwt} onChange={(event) => setField("campoUsuarioJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <p className="mt-1 text-xs text-slate-500">Ex.: login, usuario, email.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Campo da senha no payload</label>
              <input value={form.campoSenhaJwt} onChange={(event) => setField("campoSenhaJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <p className="mt-1 text-xs text-slate-500">Ex.: senha, password.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Campo do token na resposta</label>
              <input value={form.caminhoTokenJwt} onChange={(event) => setField("caminhoTokenJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <p className="mt-1 text-xs text-slate-500">Ex.: token ou data.token.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Prefixo Authorization</label>
              <input value={form.prefixoAutorizacaoJwt} onChange={(event) => setField("prefixoAutorizacaoJwt", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
              <p className="mt-1 text-xs text-slate-500">Normalmente Bearer.</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Payload enviado para gerar token</p>
              <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-slate-800">{payloadPreview}</pre>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Como o token será usado no job</p>
              <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-slate-800">{`Authorization: ${form.prefixoAutorizacaoJwt || "Bearer"} {${form.caminhoTokenJwt || "token"}}`}</pre>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          Cancelar
        </button>
        <button type="submit" className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
          Salvar autenticação
        </button>
      </div>
    </form>
  );
}

export default function AutenticacoesPage() {
  const [autenticacoes, setAutenticacoes] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingAuth, setEditingAuth] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = () => {
    setErro("");
    axios
      .get(apiUrl("/autenticacoes"))
      .then((response) => setAutenticacoes(getPayload(response) || []))
      .catch((error) => setErro(errorMessage(error, "Não foi possível carregar as autenticações.")));
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return autenticacoes;
    return autenticacoes.filter((item) => [item.descricao, item.usuarioJwt, item.urlChamadaJwt].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)));
  }, [autenticacoes, filter]);

  const openCreate = () => {
    setEditingAuth(null);
    setShowForm(true);
  };

  const openEdit = (auth) => {
    setEditingAuth(auth);
    setShowForm(true);
  };

  const saveAuth = (payload) => {
    const request = editingAuth
      ? axios.put(apiUrl(`/autenticacoes/${editingAuth.id}`), payload)
      : axios.post(apiUrl("/autenticacoes"), payload);

    request
      .then(() => {
        AlertComponent("Pronto", editingAuth ? "Autenticação atualizada." : "Autenticação cadastrada.");
        setShowForm(false);
        setEditingAuth(null);
        carregar();
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível salvar a autenticação."), { icon: "error" }));
  };

  const toggleStatus = (auth) => {
    axios
      .patch(apiUrl(`/autenticacoes/${auth.id}/status`), { ativo: !auth.ativo })
      .then(() => carregar())
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível alterar o status."), { icon: "error" }));
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Autenticações"
        subtitle="Configurações reutilizáveis para endpoints que exigem geração de JWT."
        actions={
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
            Nova autenticação
          </button>
        }
      />

      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

      <Panel
        actions={
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Buscar autenticação" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 sm:w-80" />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Descrição</th>
                <th className="px-4 py-3">JWT</th>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Token</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((auth) => (
                <tr key={auth.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{auth.descricao}</p>
                    <p className="max-w-sm truncate text-xs text-slate-500">{auth.urlChamadaJwt || "-"}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{auth.protegidaPorJwt ? "Sim" : "Não"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{auth.usuarioJwt || "-"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{auth.caminhoTokenJwt}</td>
                  <td className="px-4 py-3"><StatusBadge value={auth.ativo ? "Ativo" : "Inativo"} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" title="Editar" onClick={() => openEdit(auth)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m16.86 4.49 2.65 2.65M5 19l4.5-1 9.9-9.9a1.88 1.88 0 0 0-2.65-2.65l-9.9 9.9L5 19Z" />
                        </svg>
                      </button>
                      <button type="button" title={auth.ativo ? "Inativar" : "Ativar"} onClick={() => toggleStatus(auth)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2m5-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="Nenhuma autenticação encontrada." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {showForm && (
        <Modal title={editingAuth ? "Editar autenticação" : "Nova autenticação"} subtitle="A senha JWT deve ser informada ao salvar uma autenticação protegida." onClose={() => setShowForm(false)} width="max-w-4xl">
          <AutenticacaoForm initialAuth={editingAuth} onCancel={() => setShowForm(false)} onSave={saveAuth} />
        </Modal>
      )}
    </div>
  );
}
